import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./MyCreatedAuctionView.css";
 import BusinessIcon from "@mui/icons-material/Business";
import {
  Calendar,
  Clock,
  Users,
  FileText,
  Download,
  Building,
  Mail,
  Phone,
  MapPin,
  User,
  CheckCircle,
  XCircle,
  IndianRupee,
  DollarSign,
  Timer,
  ArrowLeft,
  RefreshCw,
  Play,
  Gavel,
  Edit,
  Settings,
  File,
  FileImage,
  FileSpreadsheet,
  FileCode,
} from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { API_BASE_URL } from "../../../services/apiConfig";
import toast from "react-hot-toast";
import { format } from "date-fns";
import AuctionService from "../../../services/auctionService";
import apiDencrimentValue from "../../../services/apiDencrimentValue";
import apiAuctionService from "../../../services/apiAuctionService";
import newAuctionService from "../../../services/newAuctionService";
import { BaseAuction, AuctionParticipant } from "../../../types/auction";
import SuccessModal from "../../Common/SuccessModal";
import ErrorModal from "../../Common/ErrorModal";
import InfoModal from "../../Common/InfoModal";
import CompanyViewerCard from "../SharedAuctions/CompanyViewerCard";
import { auctionTypeLabel, auctionModeLabel } from "../../../utils/auctionTypeLabels";
import { stepFieldLabel } from "../../../utils/auctionDirection";
// import websocketService from "../../../services/websocketService"; // Disabled - using polling instead

/* 🔧 ADD-1  helper – add minutes to HH:MM  */
/* 🔧 FIXED helper – add minutes to 12-hour string */
const addMinutesToTime = (time12: string, mins: number): string => {
  const d = new Date();
  const [time, modifier] = time12.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (modifier === 'PM' && h < 12) h += 12;
  if (modifier === 'AM' && h === 12) h = 0;
  d.setHours(h, m + mins, 0, 0);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/* Helper function to get appropriate file icon */
const getFileIcon = (fileName: string, fileType?: string) => {
  if (!fileName) {
    return <File className="w-5 h-5" style={{ color: '#6b7280' }} />;
  }
  
  const extension = fileName.split('.').pop()?.toLowerCase();
  const type = fileType?.toLowerCase();
  
  if (type?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension || '')) {
    return <FileImage className="w-5 h-5" style={{ color: '#10b981' }} />;
  }
  if (type?.includes('spreadsheet') || ['xlsx', 'xls', 'csv'].includes(extension || '')) {
    return <FileSpreadsheet className="w-5 h-5" style={{ color: '#059669' }} />;
  }
  if (['pdf'].includes(extension || '') || type?.includes('pdf')) {
    return <FileText className="w-5 h-5" style={{ color: '#dc2626' }} />;
  }
  if (['doc', 'docx'].includes(extension || '') || type?.includes('document')) {
    return <FileText className="w-5 h-5" style={{ color: '#2563eb' }} />;
  }
  if (['txt', 'rtf'].includes(extension || '') || type?.includes('text')) {
    return <FileCode className="w-5 h-5" style={{ color: '#6b7280' }} />;
  }
  
  return <File className="w-5 h-5" style={{ color: '#6b7280' }} />;
};

const MyCreatedAuctionView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [auction, setAuction] = useState<BaseAuction | null>(null);
  const [participants, setParticipants] = useState<AuctionParticipant[]>([]);
  const [auctioneer, setAuctioneer] = useState<any>(null);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [preBids, setPreBids] = useState<any[]>([]);
  const [preBidsLoading, setPreBidsLoading] = useState(false);
  const [isLiveUpdating, setIsLiveUpdating] = useState(false);
  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [allParticipants, setAllParticipants] = useState<any[]>([]);
  const [isEditDecrementOpen, setIsEditDecrementOpen] = useState(false);
  const [newDecrementValue, setNewDecrementValue] = useState<number | string>(
    ""
  );
  const [updatingDecrement, setUpdatingDecrement] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [auctionDocuments, setAuctionDocuments] = useState<any[]>([]); // Store documents permanently
  const isMountedRef = useRef(true);
  const previousPreBidIdsRef = useRef<Set<string>>(new Set()); // Track seen pre-bid IDs
  const notificationShownRef = useRef(false); // Track if notification has been shown
  const lastNotifiedStatusRef = useRef<string | null>(null); // Track last status we showed notification for
  const { user } = useAuth();
  
  // Modal states
  const [successModal, setSuccessModal] = useState({ isOpen: false, message: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  const [infoModal, setInfoModal] = useState({ isOpen: false, message: '' });

  // Initial data load - only runs once when component mounts or ID changes
  useEffect(() => {
    // Reset tracking refs on new auction
    previousPreBidIdsRef.current = new Set();
    notificationShownRef.current = false;
    lastNotifiedStatusRef.current = null; // Reset status notification tracker
    loadAuctionDetails();
    fetchUserDetails();
    fetchPreBids();
    fetchAllParticipants();
  }, [id]);

  // WebSocket connection disabled - using polling instead
  // The backend uses Socket.IO which requires socket.io-client
  // For now, we rely on polling (10-second intervals) which works perfectly
  // To enable WebSocket: Install socket.io-client and update websocketService
  useEffect(() => {
    if (!id) return;
    
    // WebSocket temporarily disabled to avoid connection errors
    // Polling provides reliable real-time updates every 10 seconds

    return () => {
    };
  }, [id]);

  // Polling as fallback for real-time updates
  useEffect(() => {
    if (!id || !auction) return;

    const pollInterval = setInterval(() => {
      if (!isMountedRef.current) return;
      
      // Silent polling - no logs to avoid console spam
      fetchPreBids(true); // Silent update
      fetchAllParticipants(true); // Silent update
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [id, auction]);

  // Real-time status update polling - similar to ParticipantAuctionView
  useEffect(() => {
    if (!auction || !id) return;

    // Function to check if status should change based on current time
    const checkStatusChange = () => {
      const now = new Date();
      const auctionDate = new Date(auction.auctionDate);
      
      // Parse start time correctly - handle 12-hour format
      const startTimeStr = auction.auctionStartTime;
      let startDateTime: Date;
      
      if (startTimeStr.includes('AM') || startTimeStr.includes('PM')) {
        const [time, modifier] = startTimeStr.split(' ');
        let [hours, minutes] = time.split(':').map(Number);
        
        if (modifier === 'PM' && hours < 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;
        
        startDateTime = new Date(auctionDate);
        startDateTime.setHours(hours, minutes, 0, 0);
      } else {
        const [hours, minutes] = startTimeStr.split(':').map(Number);
        startDateTime = new Date(auctionDate);
        startDateTime.setHours(hours, minutes, 0, 0);
      }
      
      // Parse end time if available
      let endDateTime: Date | null = null;
      if (auction.auctionEndTime) {
        const endTimeStr = auction.auctionEndTime;
        
        if (endTimeStr.includes('AM') || endTimeStr.includes('PM')) {
          const [time, modifier] = endTimeStr.split(' ');
          let [hours, minutes] = time.split(':').map(Number);
          
          if (modifier === 'PM' && hours < 12) hours += 12;
          if (modifier === 'AM' && hours === 12) hours = 0;
          
          endDateTime = new Date(auctionDate);
          endDateTime.setHours(hours, minutes, 0, 0);
        } else {
          const [hours, minutes] = endTimeStr.split(':').map(Number);
          endDateTime = new Date(auctionDate);
          endDateTime.setHours(hours, minutes, 0, 0);
        }
      }

      // Determine what status should be based on current time
      let expectedStatus: "upcoming" | "live" | "completed";
      if (now < startDateTime) {
        expectedStatus = "upcoming";
      } else if (endDateTime && now >= endDateTime) {
        expectedStatus = "completed";
      } else {
        expectedStatus = "live";
      }

      // If status doesn't match expected, refresh auction data
      if (auction.status !== expectedStatus) {
        
        // Only show notification if we haven't already notified for this status
        if (lastNotifiedStatusRef.current !== expectedStatus) {
          // Show notification to user
          const statusMessages = {
            live: "🔴 Auction is now LIVE!",
            completed: "✅ Auction has ended.",
            upcoming: "⏳ Auction status updated to Upcoming."
          };
          
          setInfoModal({
            isOpen: true,
            message: statusMessages[expectedStatus],
          });
          
          // Auto-close the modal after 3 seconds
          setTimeout(() => {
            if (isMountedRef.current) {
              setInfoModal({ isOpen: false, message: '' });
            }
          }, 3000);
          
          // Mark this status as notified
          lastNotifiedStatusRef.current = expectedStatus;
          
        }
        
        // Reload auction details silently to get updated status (no loading spinner)
        loadAuctionDetails(true);
      }
    };

    // Check status immediately
    checkStatusChange();

    // Poll every 10 seconds to check for status changes
    const statusCheckInterval = setInterval(() => {
      if (isMountedRef.current) {
        checkStatusChange();
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(statusCheckInterval);
  }, [id, auction]);

  // Timer to trigger status change at exact start/end time
  useEffect(() => {
    if (!auction) return;

    const now = new Date();
    const auctionDate = new Date(auction.auctionDate);
    
    // Parse start time
    const startTimeStr = auction.auctionStartTime;
    let startDateTime: Date;
    
    if (startTimeStr.includes('AM') || startTimeStr.includes('PM')) {
      const [time, modifier] = startTimeStr.split(' ');
      let [hours, minutes] = time.split(':').map(Number);
      
      if (modifier === 'PM' && hours < 12) hours += 12;
      if (modifier === 'AM' && hours === 12) hours = 0;
      
      startDateTime = new Date(auctionDate);
      startDateTime.setHours(hours, minutes, 0, 0);
    } else {
      const [hours, minutes] = startTimeStr.split(':').map(Number);
      startDateTime = new Date(auctionDate);
      startDateTime.setHours(hours, minutes, 0, 0);
    }
    
    // Parse end time
    let endDateTime: Date | null = null;
    if (auction.auctionEndTime) {
      const endTimeStr = auction.auctionEndTime;
      
      if (endTimeStr.includes('AM') || endTimeStr.includes('PM')) {
        const [time, modifier] = endTimeStr.split(' ');
        let [hours, minutes] = time.split(':').map(Number);
        
        if (modifier === 'PM' && hours < 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;
        
        endDateTime = new Date(auctionDate);
        endDateTime.setHours(hours, minutes, 0, 0);
      } else {
        const [hours, minutes] = endTimeStr.split(':').map(Number);
        endDateTime = new Date(auctionDate);
        endDateTime.setHours(hours, minutes, 0, 0);
      }
    }

    const timers: NodeJS.Timeout[] = [];

    // If auction is upcoming, set timer for start
    if (auction.status === "upcoming" && now < startDateTime) {
      const timeUntilStart = startDateTime.getTime() - now.getTime();
      if (timeUntilStart > 0 && timeUntilStart < 24 * 60 * 60 * 1000) { // Within 24 hours
        const startTimer = setTimeout(() => {
          loadAuctionDetails(true); // Silent refresh to avoid glitch
        }, timeUntilStart);
        timers.push(startTimer);
      }
    }

    // If auction is live or upcoming, set timer for end
    if (endDateTime && (auction.status === "live" || auction.status === "upcoming") && now < endDateTime) {
      const timeUntilEnd = endDateTime.getTime() - now.getTime();
      if (timeUntilEnd > 0 && timeUntilEnd < 24 * 60 * 60 * 1000) { // Within 24 hours
        const endTimer = setTimeout(() => {
          loadAuctionDetails(true); // Silent refresh to avoid glitch
        }, timeUntilEnd);
        timers.push(endTimer);
      }
    }

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [auction]);

  // Separate useEffect for live data updates (when auction is already live)
  useEffect(() => {
    if (!auction || auction.status !== "live" || !liveUpdatesEnabled) return;

    // For live auctions, continuously update data without full refresh
    const liveDataInterval = setInterval(() => {
      if (!isMountedRef.current) return;
      updateLiveAuctionData();
    }, 5000); // Update every 5 seconds for live data

    return () => clearInterval(liveDataInterval);
  }, [auction?.status, liveUpdatesEnabled])

  // Cleanup effect
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchUserDetails = async () => {
    try {
      const token =
        localStorage.getItem("authToken") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken");
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.user) {
          setUserDetails(result.user);
        }
      }
    } catch (error) {
      console.error(
        "[MyCreatedAuctionView] Failed to fetch user details:",
        error
      );
    }
  };

  // Update live auction data without full page refresh
  const updateLiveAuctionData = async () => {
    if (!id || !auction) return;

    setIsLiveUpdating(true);
    try {
      const token =
        localStorage.getItem("authToken") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken");
      if (!token) return;

      // Fetch updated auction data quietly
      const response = await fetch(`${API_BASE_URL}/auction/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.auction && isMountedRef.current) {
          // Only update the dynamic parts, preserve static data like documents
          setAuction((prevAuction) => ({
            ...prevAuction,
            ...data.auction,
            // Keep existing static data if new data doesn't include it
            title: data.auction.title || prevAuction?.title,
            // IMPORTANT: Preserve documents from initial load
            documents: prevAuction?.documents || data.auction.documents || [],
            // Preserve other static fields
            auctionDetails: prevAuction?.auctionDetails || data.auction.description,
            auctioneerCompany: prevAuction?.auctioneerCompany || data.auction.auctioneer_company_name,
            auctioneerPhone: prevAuction?.auctioneerPhone || data.auction.auctioneer_phone,
            auctioneerAddress: prevAuction?.auctioneerAddress || data.auction.auctioneer_address,
          }));

          setLastUpdated(new Date());
        }
      }

      // Also update pre-bids and participants if they might have changed (but only if not already loading)
      if (!preBidsLoading) {
        const preBidsResponse = await fetch(
          `${API_BASE_URL}/auction/${id}/prebids`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (preBidsResponse.ok) {
          const preBidsResult = await preBidsResponse.json();
          if (
            preBidsResult.success &&
            preBidsResult.prebids &&
            isMountedRef.current
          ) {
            setPreBids(preBidsResult.prebids);
          }
        }
      }

      // Update participants data if not already loading
      if (!participantsLoading) {
        const participantsResponse = await fetch(
          `${API_BASE_URL}/auction/${id}/participants`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (participantsResponse.ok) {
          const participantsResult = await participantsResponse.json();
          if (
            participantsResult.success &&
            participantsResult.participants &&
            isMountedRef.current
          ) {
            setAllParticipants(participantsResult.participants);
          }
        }
      }
    } catch (error) {
      console.error("[MyCreatedAuctionView] Error updating live data:", error);
      // Don't show error to user for background updates
    } finally {
      setIsLiveUpdating(false);
    }
  };

  // Fetch all participants for this auction with complete information
const fetchAllParticipants = async (silent: boolean = false) => {
  if (!id) return;
  
  // Only show loading for manual fetches
  if (!silent) {
    setParticipantsLoading(true);
  }
  
  try {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token") || localStorage.getItem("accessToken");
    if (!token) return;

    const response = await fetch(`${API_BASE_URL}/auction/${id}/participants`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      const result = await response.json();
      if (!silent) {
         // Debug log only for manual fetches
      }
      
      if (result.success && result.participants && Array.isArray(result.participants)) {
        setAllParticipants(result.participants);
      } else if (result.success && Array.isArray(result.data)) {
        // Handle different API response structure
        setAllParticipants(result.data);
      }
    } else {
      // Only log non-silent failures
      if (!silent) {
        console.error("Failed to fetch participants:", response.status);
      }
    }
  } catch (error: any) {
    // Only log errors for non-silent fetches
    if (!silent) {
      console.error("Error fetching participants:", error);
    }
    // Silently ignore CORS and network errors during polling
    if (silent && (error.name === 'TypeError' || error.message?.includes('fetch'))) {
      return;
    }
  } finally {
    if (!silent) {
      setParticipantsLoading(false);
    }
  }
};
  
  // Fetch pre-bids for this auction with smart update detection
  const fetchPreBids = async (silent: boolean = false) => {
    if (!id) return;
    
    // Only show loading spinner for manual/initial fetches
    if (!silent) {
      setPreBidsLoading(true);
    }
    
    try {
      const token =
        localStorage.getItem("authToken") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken");
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/auction/${id}/prebids`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.prebids) {
          const allPreBids = result.prebids;
          
          // Filter to keep only the latest pre-bid from each participant
          // Group by user_id or phone_number to identify unique participants
          const latestPreBidsMap = new Map<string, any>();
          
          allPreBids.forEach((preBid: any) => {
            // Use user_id or phone_number as unique identifier for participant
            const participantKey = preBid.user_id?.toString() || preBid.phone_number || preBid.person_name || '';
            
            if (participantKey) {
              const existingBid = latestPreBidsMap.get(participantKey);
              
              // If no existing bid for this participant, or this bid is newer, keep it
              if (!existingBid) {
                latestPreBidsMap.set(participantKey, preBid);
              } else {
                // Compare timestamps to keep the latest one
                const existingTime = new Date(existingBid.created_at || existingBid.bid_time || 0).getTime();
                const newTime = new Date(preBid.created_at || preBid.bid_time || 0).getTime();
                
                if (newTime > existingTime) {
                  latestPreBidsMap.set(participantKey, preBid);
                }
              }
            }
          });
          
          // Convert map back to array - only the latest bid from each participant
          const newPreBids = Array.from(latestPreBidsMap.values());
          
          // Create unique IDs for each pre-bid
          const currentPreBidIds = new Set<string>(
            newPreBids.map((pb: any) => 
              pb.id?.toString() || `${pb.user_id}_${pb.amount}_${pb.created_at}`
            )
          );
          
          // Find genuinely new pre-bids that weren't in the previous set
          const genuinelyNewPreBids = newPreBids.filter((pb: any) => {
            const preBidId = pb.id?.toString() || `${pb.user_id}_${pb.amount}_${pb.created_at}`;
            return !previousPreBidIdsRef.current.has(preBidId);
          });
          
          // Update the tracking ref with current IDs
          previousPreBidIdsRef.current = currentPreBidIds;
          
          // Update state with filtered pre-bids (only latest from each participant)
          setPreBids(newPreBids);
          setLastUpdated(new Date());
          
          
          // Show notification ONLY ONCE for new pre-bids during silent updates
          if (genuinelyNewPreBids.length > 0 && silent && isMountedRef.current && !notificationShownRef.current) {
            const newCount = genuinelyNewPreBids.length;
            
            notificationShownRef.current = true; // Mark notification as shown
            
            setSuccessModal({ 
              isOpen: true, 
              message: `${newCount} new pre-bid${newCount > 1 ? 's' : ''} received!` 
            });
            
            // Auto-close notification and reset flag
            setTimeout(() => {
              setSuccessModal({ isOpen: false, message: '' });
              // Reset flag after a cooldown period to allow next genuine notification
              setTimeout(() => {
                notificationShownRef.current = false;
              }, 5000); // 5 second cooldown before next notification can show
            }, 3000);
          }
        }
      } else {
        // Only log non-silent failures
        if (!silent) {
          console.warn(
            "[MyCreatedAuctionView] Failed to fetch pre-bids:",
            response.status
          );
        }
      }
    } catch (error: any) {
      // Only log errors for non-silent fetches to avoid console spam during polling
      if (!silent) {
        console.error("[MyCreatedAuctionView] Error fetching pre-bids:", error);
      }
      // Silently ignore CORS and network errors during polling
      if (silent && (error.name === 'TypeError' || error.message?.includes('fetch'))) {
        // Network error during polling - ignore silently
        return;
      }
    } finally {
      if (!silent) {
        setPreBidsLoading(false);
      }
    }
  };

  // Handle pre-bid approval/rejection
  const handlePreBidAction = async (
    preBidId: string,
    action: "approve" | "reject"
  ) => {
    try {
      const token =
        localStorage.getItem("authToken") ||
        localStorage.getItem("token") ||
        localStorage.getItem("accessToken");
      if (!token) return;

      const response = await fetch(
        `${API_BASE_URL}/auction/prebid/${preBidId}/${action}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSuccessModal({ isOpen: true, message: `Pre-bid ${action}d successfully` });
          fetchPreBids(); // Refresh the pre-bids list
          fetchAllParticipants(); // Refresh participants data
          // Also update live auction data to get latest statistics
          if (auction?.status === "live" || auction?.status === "upcoming") {
            updateLiveAuctionData();
          }
        } else {
          setErrorModal({ isOpen: true, message: result.message || `Failed to ${action} pre-bid` });
        }
      } else {
        setErrorModal({ isOpen: true, message: `Failed to ${action} pre-bid. Please try again.` });
      }
    } catch (error) {
      console.error(
        `[MyCreatedAuctionView] Error ${action}ing pre-bid:`,
        error
      );
      setErrorModal({ isOpen: true, message: `Failed to ${action} pre-bid. Please try again.` });
    }
  };

  const loadAuctionDetails = async (silent: boolean = false) => {
    if (!id) {
      navigate("/dashboard/auctions");
      return;
    }
    // Only show loading spinner on initial load, not on status updates
    if (!silent) {
      setLoading(true);
    }
    try {
      let auctionData: BaseAuction | null = null;

      // Try new auction details API first
      try {
        const AUCTION_API_BASE_URL = `${API_BASE_URL}/auction`;
        const token =
          localStorage.getItem("authToken") ||
          localStorage.getItem("token") ||
          localStorage.getItem("accessToken");

        const response = await fetch(`${API_BASE_URL}/auction/${id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.auction) {
            const rawAuction = result.auction;

            // This is the auctioneer's console (registered participants,
            // pre-bid moderation...). Anyone who isn't the creator -- or an
            // admin -- belongs on the participant view instead.
            if (rawAuction.is_creator === false && !rawAuction.is_admin) {
              navigate(`/dashboard/auction/${id}`, { replace: true });
              return;
            }

            // Map the new API response to BaseAuction format
            auctionData = {
              id: rawAuction.id.toString(),
              backendId: rawAuction.id.toString(),
              title: rawAuction.title,
              auctionNo: rawAuction.auction_no,
              auctionDate: rawAuction.auction_date, // live
              auctionStartTime: rawAuction.formatted_start_time, // live
              auctionEndTime: rawAuction.formatted_end_time, // live (may be null)
              duration: rawAuction.duration, // live
              currency: rawAuction.currency as "INR" | "USD", // live
              auctionDetails: rawAuction.description,
              preBidOfferAllowed: Boolean(rawAuction.pre_bid_allowed), // live (0/1 → false/true)
              decrementalValue: Number(rawAuction.decremental_value),
              startingPrice: Number(rawAuction.current_price),
              reservePrice: undefined,
              status: rawAuction.status as "upcoming" | "live" | "completed",
              participants:
                rawAuction.participants?.map((p: any) => p.phone_number) || [],
              documents:
                rawAuction.documents?.map((d: any) => {
                  // Extract file extension to determine type if not provided
                  const fileName = d.file_name || d.original_name || d.name || "Unknown Document";
                  const fileExtension = fileName.split('.').pop()?.toLowerCase();
                  
                  // Determine file type based on extension if not provided
                  let fileType = d.file_type || d.mimetype || d.mime_type;
                  if (!fileType && fileExtension) {
                    const typeMap: { [key: string]: string } = {
                      'pdf': 'application/pdf',
                      'doc': 'application/msword',
                      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                      'jpg': 'image/jpeg',
                      'jpeg': 'image/jpeg',
                      'png': 'image/png',
                      'gif': 'image/gif'
                    };
                    fileType = typeMap[fileExtension] || 'application/octet-stream';
                  }
                  
                  // Format file size properly
                  let fileSize = "Unknown Size";
                  if (d.file_size && !isNaN(d.file_size)) {
                    const sizeInBytes = Number(d.file_size);
                    if (sizeInBytes < 1024) {
                      fileSize = `${sizeInBytes} B`;
                    } else if (sizeInBytes < 1024 * 1024) {
                      fileSize = `${(sizeInBytes / 1024).toFixed(1)} KB`;
                    } else {
                      fileSize = `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
                    }
                  }
                  
                  return {
                    name: fileName,
                    url: d.file_url || d.url || d.file_path || d.path || "",
                    size: fileSize,
                    type: fileType,
                    originalName: d.original_name || d.file_name || d.name || fileName,
                    uploadedAt: d.uploaded_at || d.created_at,
                  };
                }) || [],
              createdBy: rawAuction.created_by.toString(),
              createdAt: rawAuction.created_at,
              updatedAt: rawAuction.updated_at,

              auctioneerCompany: rawAuction.creator_info?.company_name,
              auctioneerPhone: rawAuction.creator_info?.phone,
              auctioneerAddress: "",
            } as BaseAuction;

            // (auctionData as any).backend = { ...rawAuction, user: rawAuction.creator_info };

            // Store raw backend data for additional fields
            (auctionData as any).backend = {
              ...rawAuction,
              user: result.user,
              auctioneer: result.auctioneer || result.user,
              creator_info: result.user,
            };

            // Store auctioneer company details
            auctionData.auctioneerCompany =
              rawAuction.auctioneer_company_name || rawAuction.company_name;
            auctionData.auctioneerPhone =
              rawAuction.auctioneer_phone || rawAuction.phone_number;
            auctionData.auctioneerAddress =
              rawAuction.auctioneer_address || rawAuction.company_address;
            (auctionData as any).auctioneerEmail =
              rawAuction.auctioneer_email || rawAuction.email;
            (auctionData as any).auctioneerPerson =
              result.user?.person_name || rawAuction.person_name;

            if (result.user) {
              setUserDetails({
                company_name:
                  rawAuction.auctioneer_company_name ||
                  result.user.company_name,
                person_name: result.user.person_name,
                email: rawAuction.auctioneer_email || result.user.email,
                phone_number:
                  rawAuction.auctioneer_phone || result.user.phone_number,
                company_address:
                  rawAuction.auctioneer_address || result.user.company_address,
              });
            }

            
            // Enhanced document debugging
            if (rawAuction.documents) {
              
              if (auctionData.documents && auctionData.documents.length > 0) {
                auctionData.documents.forEach((doc, index) => {
                });
              } else {
                console.warn("[MyCreatedAuctionView] No documents found after processing");
              }
            } else {
            }
          }
        }
      } catch (newApiErr: any) {
        console.warn(
          "[MyCreatedAuctionView] New auction details API failed, trying legacy:",
          newApiErr
        );
      }

      // Fallback to legacy API if new API failed
      if (!auctionData) {
        try {
          auctionData = await apiAuctionService.fetchAuctionById(id);
        } catch (apiErr: any) {
          console.warn(
            "Legacy API fetch failed, falling back to local storage:",
            apiErr
          );
          auctionData = AuctionService.getAuctionById(id);
        }
      }

      if (!auctionData) {
        setErrorModal({ isOpen: true, message: "Auction not found. Redirecting..." });
        setTimeout(() => navigate("/dashboard/auctions"), 2000);
        return;
      }

      setAuction(auctionData);
      
      // Store documents separately to prevent them from being lost during updates
      if (auctionData.documents && auctionData.documents.length > 0) {
        setAuctionDocuments(auctionData.documents);
      }

      /* 🔧 ADD-2  fix end-time only for upcoming auctions */
if (auctionData && auctionData.status === 'upcoming') {
  auctionData.auctionEndTime = addMinutesToTime(
    auctionData.auctionStartTime,
    auctionData.duration
  );
}
      
      const participantData = AuctionService.getParticipantsByAuction(id);
      setParticipants(participantData);
      const auctioneerData = AuctionService.getUserById(auctionData.createdBy);
      setAuctioneer(auctioneerData);

      // Refresh pre-bids when auction details are loaded
      fetchPreBids();
    } catch (error) {
      console.error("Error loading auction details:", error);
      // Only show error modal on initial load, not on silent updates
      if (!silent) {
        setErrorModal({ isOpen: true, message: "Failed to load auction details. Redirecting..." });
        setTimeout(() => navigate("/dashboard/auctions"), 2000);
      }
    } finally {
      // Only clear loading state if it was set (non-silent mode)
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // const handleDownloadDocument = (doc: { name: string; url: string }) => {
  //   toast.success(`Downloading ${doc.name}`);
  //   console.log(`Download: ${doc.name} from ${doc.url}`);
  // };
  
  const handleDownloadDocument = async (doc: { name: string; url: string; type?: string; originalName?: string }) => {
    
    // Validate input
    if (!doc || typeof doc !== 'object') {
      setErrorModal({ isOpen: true, message: "Invalid document data" });
      console.error("Invalid document object:", doc);
      return;
    }
    
    if (!doc.url) {
      setErrorModal({ isOpen: true, message: "Download link is missing for this document" });
      console.error("Document URL is missing:", doc);
      return;
    }

    if (!doc.name) {
      setErrorModal({ isOpen: true, message: "Document name is missing" });
      console.error("Document name is missing:", doc);
      return;
    }

    try {
      // Clean and construct proper URL
      let downloadUrl = doc.url.trim();
      
      // Handle different URL formats
      if (downloadUrl.startsWith('/uploads/') || downloadUrl.startsWith('uploads/')) {
        // Relative URL - make it absolute
        const cleanPath = downloadUrl.startsWith('/') ? downloadUrl : '/' + downloadUrl;
        downloadUrl = `${API_BASE_URL}${cleanPath}`;
      } else if (downloadUrl.startsWith('/')) {
        // Other relative URLs
        downloadUrl = `${API_BASE_URL}${downloadUrl}`;
      } else if (!downloadUrl.startsWith('http')) {
        // No protocol, assume relative
        downloadUrl = `${API_BASE_URL}/${downloadUrl}`;
      }
      
      // CRITICAL FIX: Ensure HTTPS is used to avoid Mixed Content errors
      if (downloadUrl.startsWith('http://')) {
        downloadUrl = downloadUrl.replace('http://', 'https://');
      }


      const fileName = doc.originalName || doc.name || "download";
      
      // Use fetch with blob to force download instead of preview
      const token = localStorage.getItem("authToken") || 
                   localStorage.getItem("token") || 
                   localStorage.getItem("accessToken");

      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: token ? { 
          Authorization: `Bearer ${token}`,
        } : {},
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("File not found on server");
        } else if (response.status === 403 || response.status === 401) {
          throw new Error("Access denied - please check authentication");
        } else {
          throw new Error(`Server error: ${response.status}`);
        }
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Check if we got actual file content
      if (blob.size === 0) {
        throw new Error("Empty file received from server");
      }

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName; // This forces download instead of preview
      link.style.display = "none";
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      setSuccessModal({ isOpen: true, message: `Downloaded ${fileName} successfully!` });
      
    } catch (error: any) {
      console.error("Download error:", error);
      setErrorModal({ isOpen: true, message: error.message || `Failed to download ${doc.name}. Please try again later.` });
    }
  };

  // Editing is open only until the auction goes live; the API enforces it too.
  const canEditAuction = auction?.status === "upcoming";

  const handleEditAuction = () => {
    if (canEditAuction) {
      navigate(`/dashboard/edit-auction/${auction.id}`);
    } else {
      setErrorModal({
        isOpen: true,
        message: "This auction is live or has ended, so it can no longer be edited.",
      });
    }
  };

  const handleStartAuction = () => {
    if (auction?.status === "upcoming") {
      setInfoModal({ isOpen: true, message: "Starting auction..." });
      setTimeout(() => navigate(`/dashboard/auctioneer-live/${auction.id}`), 1000);
    } else {
      setErrorModal({ isOpen: true, message: "Auction cannot be started" });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const formatDateWithTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="view-auction-container">
        <div className="auction-details-card">
          <div className="loading-spinner">
            <p>Loading auction details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="view-auction-container">
        <div className="auction-details-card">
          <div className="error-message">
            <XCircle className="w-5 h-5" />
            <div>
              <h3>Auction Not Found</h3>
              <p>The requested auction could not be found.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper: backend meta if present
  const backendMeta: any = (auction as any).backend;
  const backendParticipants =
    allParticipants.length > 0
      ? allParticipants
      : backendMeta?.participantsList || [];
  const bidHistory = backendMeta?.bidHistory || [];

  // ---- derived display values ----
  const auctionTypeValue: string | undefined =
    backendMeta?.auction_type ?? (auction as any).auction_type;
  const auctionModeValue: string | undefined =
    backendMeta?.auction_mode ?? (auction as any).auction_mode;
  const stepLabel = stepFieldLabel(auctionModeValue);
  const stats = backendMeta?.statistics;
  const companyName =
    userDetails?.company_name ||
    backendMeta?.user?.company_name ||
    backendMeta?.creator_info?.company_name ||
    auctioneer?.companyName ||
    auction.auctioneerCompany ||
    "Unknown Company";
  const personName =
    userDetails?.person_name ||
    backendMeta?.user?.person_name ||
    backendMeta?.creator_info?.person_name ||
    auctioneer?.personName ||
    backendMeta?.auctioneerPerson ||
    "Unknown Person";
  const emailValue =
    userDetails?.email ||
    backendMeta?.user?.email ||
    backendMeta?.creator_info?.email ||
    auctioneer?.email ||
    auctioneer?.mailId ||
    backendMeta?.auctioneerEmail ||
    "Unknown Email";
  const addressValue =
    userDetails?.company_address ||
    backendMeta?.user?.company_address ||
    backendMeta?.creator_info?.company_address ||
    auctioneer?.companyAddress ||
    backendMeta?.auctioneerAddress ||
    "Unknown Address";
  const timeOf = (v: string) =>
    new Date(v).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  const getFileTypeName = (type: string) => {
    if (type.includes("pdf")) return "PDF Document";
    if (type.includes("word") || type.includes("doc")) return "Word Document";
    if (type.includes("image") || type.includes("jpeg") || type.includes("png")) return "Image";
    if (type.includes("spreadsheet") || type.includes("excel")) return "Spreadsheet";
    return type.split("/").pop()?.toUpperCase() || "Document";
  };

  return (
    <div className="view-auction-container mcv-page">
      <button
        type="button"
        onClick={() => navigate("/dashboard/MyCreatedA")}
        className="mcv-back"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Auctions
      </button>

      {/* Header */}
      <header className="mcv-header">
        <div className="mcv-header-info">
          <h1 className="mcv-title">{auction.title}</h1>
          <div className="mcv-meta">
            <span className="mcv-auction-no">
              <Gavel /> {auction.auctionNo}
            </span>
            <span className="mcv-meta-text">
              {auction.rawAuctionDate
                ? formatDate(auction.rawAuctionDate)
                : formatDate(auction.auctionDate)}{" "}
              · {auction.auctionStartTime}
              {auction.auctionEndTime ? ` – ${auction.auctionEndTime}` : ""}
            </span>
          </div>
          <div className="mcv-tags">
            <span className="mcv-tag mcv-tag-type">
              {auctionTypeLabel(auctionTypeValue)}
            </span>
            <span className="mcv-tag mcv-tag-mode">
              {auctionModeLabel(auctionModeValue)}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.6rem" }}>
          <span className={`mcv-status mcv-status-${auction.status}`}>
            <span className="mcv-status-dot" />
            {auction.status.toUpperCase()}
          </span>
          <button
            type="button"
            className="mcv-btn"
            onClick={handleEditAuction}
            disabled={!canEditAuction}
            title={
              canEditAuction
                ? "Edit this auction"
                : "Editing is locked once the auction is live"
            }
          >
            <Edit className="w-4 h-4" />
            {canEditAuction ? "Edit Auction" : "Editing locked"}
          </button>
        </div>
      </header>

      {/* Key figures */}
      <section className="mcv-stats">
        <div className="mcv-stat mcv-stat-lead">
          <span className="mcv-stat-label">
            {auctionModeValue === "forward" ? "Highest Bid" : "Lowest Bid"}
          </span>
          <span className="mcv-stat-value">
            {stats && (auctionModeValue === "forward" ? stats.highest_bid : stats.lowest_bid)
              ? `${auction.currency} ${Number(
                  auctionModeValue === "forward" ? stats.highest_bid : stats.lowest_bid
                ).toLocaleString()}`
              : "No bids yet"}
          </span>
          <span className="mcv-stat-hint">Current best offer</span>
        </div>
        <div className="mcv-stat">
          <span className="mcv-stat-label">Total Bids</span>
          <span className="mcv-stat-value">{stats?.total_bids || 0}</span>
          <span className="mcv-stat-hint">
            {isLiveUpdating ? (
              "Refreshing..."
            ) : auction.status === "live" ? (
              <>
                <span
                  className={`mcv-live-dot ${liveUpdatesEnabled ? "is-on" : "is-paused"}`}
                />
                {liveUpdatesEnabled ? "Live updates on" : "Live updates paused"}
                {lastUpdated && ` · ${lastUpdated.toLocaleTimeString()}`}
              </>
            ) : (
              "Across all participants"
            )}
          </span>
        </div>
        <div className="mcv-stat">
          <span className="mcv-stat-label">Participants</span>
          <span className="mcv-stat-value">
            {stats ? `${stats.active_participants || 0} / ${stats.total_participants || 0}` : backendParticipants.length}
          </span>
          <span className="mcv-stat-hint">Active / invited</span>
        </div>
        <div className="mcv-stat">
          <span className="mcv-stat-label">{stepLabel}</span>
          <span className="mcv-stat-value">
            {auction.decrementalValue
              ? `${auction.currency} ${auction.decrementalValue.toLocaleString()}`
              : "—"}
          </span>
          <span className="mcv-stat-hint">Minimum bid change</span>
        </div>
      </section>

      {/* Edit Decrement Modal */}
      {isEditDecrementOpen && (
        <div className="prebuild-modal-overlay" role="dialog" aria-modal="true">
          <div className="prebuild-modal">
            <h3 className="prebuild-modal-title">Edit Decrement Value</h3>
            <div style={{ marginTop: 8 }}>
              <label className="modal-label">
                Value ({auction?.currency || "₹"})
              </label>
              <input
                type="number"
                className="prebuild-input"
                value={String(newDecrementValue)}
                onChange={(e) => setNewDecrementValue(e.target.value)}
                disabled={updatingDecrement}
              />
            </div>
            <div className="prebuild-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setIsEditDecrementOpen(false)}
                disabled={updatingDecrement}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={async () => {
                  // validation
                  const num = Number(newDecrementValue);
                  if (Number.isNaN(num) || num <= 0) {
                    setErrorModal({ isOpen: true, message: "Please enter a valid positive number" });
                    return;
                  }
                  setUpdatingDecrement(true);
                  try {
                    const resp =
                      await apiDencrimentValue.updateAuctionDecrement(
                        id || "",
                        num
                      );
                    if (resp && resp.success) {
                      setSuccessModal({ isOpen: true, message: "Decrement value updated successfully" });
                      // refresh auction details
                      await loadAuctionDetails();
                      setIsEditDecrementOpen(false);
                    } else {
                      setErrorModal({ isOpen: true, message:
                        resp?.message || "Failed to update decrement"
                      });
                    }
                  } catch (err: any) {
                    console.error("Update decrement error", err);
                    setErrorModal({ isOpen: true, message: err?.message || "Failed to update decrement" });
                  } finally {
                    setUpdatingDecrement(false);
                  }
                }}
                disabled={updatingDecrement}
              >
                {updatingDecrement ? "Updating..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}


      <div className="mcv-layout">
        <div className="mcv-main">
          {/* Description */}
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <FileText /> Auction Details / Description
              </h2>
            </div>
            <div className="mcv-card-body">
              <p className="mcv-description">
                {auction.auctionDetails || "No description provided."}
              </p>
            </div>
          </section>

          {/* Company viewer (2nd auctioneer) -- renders only for channel partners */}
          {id && (
            <CompanyViewerCard
              auctionId={id}
              ended={auction.status === "completed"}
            />
          )}

          {/* Auction Documents */}
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <FileText /> Auction Documents
                {auctionDocuments && auctionDocuments.length > 0 && (
                  <span className="mcv-count">{auctionDocuments.length}</span>
                )}
              </h2>
            </div>
            {auctionDocuments && auctionDocuments.length > 0 ? (
              <ul className="mcv-list">
                {auctionDocuments.map((doc, index) => {
                  const docName = doc.name || doc.originalName || `Document ${index + 1}`;
                  const docUrl = doc.url || "";
                  const docType = doc.type || "Unknown Type";
                  return (
                    <li key={index} className="mcv-row">
                      <div className="mcv-file-icon">{getFileIcon(docName, docType)}</div>
                      <div className="mcv-row-main">
                        <div className="mcv-row-title" title={doc.originalName || docName}>
                          {doc.originalName || docName}
                        </div>
                        <div className="mcv-row-sub">
                          {getFileTypeName(docType)}
                          {doc.size && doc.size !== "Unknown Size" ? ` · ${doc.size}` : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          try {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!docUrl) {
                              setErrorModal({ isOpen: true, message: "Download URL is missing for this document" });
                              console.error("Document missing URL:", doc);
                              return;
                            }
                            handleDownloadDocument({
                              ...doc,
                              name: docName,
                              url: docUrl,
                            });
                          } catch (error) {
                            console.error("Button click error:", error);
                            setErrorModal({ isOpen: true, message: "Download failed. Please try again." });
                          }
                        }}
                        className="mcv-btn"
                        title={docUrl ? `Download ${docName}` : "Download URL missing"}
                        disabled={!docUrl}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="mcv-empty">
                <FileText />
                <h3>No Documents Available</h3>
                <p>No documents have been uploaded for this auction yet.</p>
                <small>Documents can be uploaded when creating or editing the auction.</small>
              </div>
            )}
          </section>

          {/* Pre-bid Management */}
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <Gavel /> Pre-bid Management
                <span className="mcv-count">{preBids.length}</span>
              </h2>
            </div>
            {preBidsLoading ? (
              <div className="mcv-empty">
                <p>Loading pre-bids...</p>
              </div>
            ) : preBids.length > 0 ? (
              <ul className="mcv-list">
                {preBids.map((preBid: any, index: number) => (
                  <li key={preBid.id || index} className="mcv-row">
                    <div className="mcv-avatar">
                      {(preBid.person_name || preBid.company_name || "B")
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <div className="mcv-row-main">
                      <div className="mcv-row-title">
                        {preBid.person_name || preBid.company_name || "Bidder"}
                      </div>
                      <div className="mcv-row-sub">
                        {preBid.company_name || preBid.phone_number || "N/A"}
                        {(preBid.created_at || preBid.bid_time) &&
                          ` · ${formatDate(preBid.created_at || preBid.bid_time)} ${timeOf(
                            preBid.created_at || preBid.bid_time
                          )}`}
                      </div>
                    </div>
                    <div className="mcv-amount">
                      {auction?.currency === "USD" ? (
                        <DollarSign className="w-4 h-4" />
                      ) : (
                        <IndianRupee className="w-4 h-4" />
                      )}
                      {auction?.currency}{" "}
                      {Number(preBid.amount || preBid.bid_amount).toLocaleString()}
                    </div>
                    {(!preBid.status || preBid.status === "pending") && (
                      <button
                        type="button"
                        onClick={() => handlePreBidAction(preBid.id, "reject")}
                        className="mcv-btn mcv-btn-danger"
                        title="Reject pre-bid"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mcv-empty">
                <Gavel />
                <h3>No Pre-bids Yet</h3>
                <p>No pre-bids have been submitted for this auction yet.</p>
              </div>
            )}
          </section>

          {/* Registered Participants */}
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <Users /> Registered Participants
                {backendParticipants.length > 0 && (
                  <span className="mcv-count">{backendParticipants.length}</span>
                )}
              </h2>
              <button
                type="button"
                onClick={() => fetchAllParticipants(false)}
                className="mcv-btn mcv-btn-sm"
                title="Refresh participants data"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>

            {backendParticipants.length > 0 ? (
              <ul className="mcv-list">
                {backendParticipants
                  .filter((p: any) =>
                    ["invited", "joined"].includes(p.status || "invited")
                  )
                  .map((participant: any, index: number) => (
                    <li key={participant.id || index} className="mcv-row">
                      <div className="mcv-avatar">
                        {(participant.person_name || participant.email || participant.invite_email || participant.user_email || participant.phone_number || "?")
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <div className="mcv-row-main">
                        <div className="mcv-row-title">
                          {participant.person_name || "N/A"}
                        </div>
                        <div className="mcv-row-sub">
                          {participant.company_name
                            ? `${participant.company_name} • `
                            : ""}
                          {participant.email || participant.invite_email || participant.user_email || participant.phone_number}
                          {participant.user_id && ` • User ID ${participant.user_id}`}
                        </div>
                        <div className="mcv-row-times">
                          {participant.invited_at && (
                            <span>Invited {formatDate(participant.invited_at)}</span>
                          )}
                          {participant.joined_at && (
                            <span>
                              Joined {formatDate(participant.joined_at)}{" "}
                              {timeOf(participant.joined_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`mcv-chip mcv-chip-${
                          participant.status === "joined" ? "joined" : "invited"
                        }`}
                      >
                        {participant.status || "invited"}
                      </span>
                    </li>
                  ))}
              </ul>
            ) : participants.length === 0 ? (
              <div className="mcv-empty">
                <Users />
                <h3>No Participants Yet</h3>
                <p>No participants registered for this auction yet.</p>
              </div>
            ) : (
              <ul className="mcv-list">
                {participants.map((participant, index) => (
                  <li key={index} className="mcv-row">
                    <div className="mcv-avatar">
                      {participant.personName.charAt(0).toUpperCase()}
                    </div>
                    <div className="mcv-row-main">
                      <div className="mcv-row-title">{participant.personName}</div>
                      <div className="mcv-row-sub">{participant.companyName}</div>
                    </div>
                    <span className="mcv-chip mcv-chip-joined">Active</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Bid History (Backend) */}
          {bidHistory.length > 0 && (
            <section className="mcv-card">
              <div className="mcv-card-header">
                <h2 className="mcv-card-title">
                  <Clock /> Bid History
                  <span className="mcv-count">{bidHistory.length}</span>
                </h2>
              </div>
              <ul className="mcv-list">
                {bidHistory
                  .slice()
                  .sort(
                    (a: any, b: any) =>
                      new Date(b.bid_time).getTime() - new Date(a.bid_time).getTime()
                  )
                  .map((b: any) => (
                    <li key={b.id} className="mcv-row">
                      <div className="mcv-avatar">
                        {(b.person_name || "B").charAt(0)}
                      </div>
                      <div className="mcv-row-main">
                        <div className="mcv-row-title">
                          {b.company_name || b.person_name || "Bidder"}
                        </div>
                        <div className="mcv-row-sub">
                          {formatDate(b.bid_time)} {new Date(b.bid_time).toLocaleTimeString()}
                        </div>
                      </div>
                      <div className={`mcv-amount ${b.is_winning ? "is-winning" : ""}`}>
                        {auction.currency} {Number(b.amount).toLocaleString()}
                      </div>
                    </li>
                  ))}
              </ul>
            </section>
          )}
        </div>

        <aside className="mcv-side">
          {/* Company Details */}
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <BusinessIcon className="w-5 h-5" /> Your Company Details
              </h2>
            </div>
            <dl className="mcv-dl">
              <div className="mcv-dl-row">
                <dt>Company Name</dt>
                <dd>{companyName}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Person Name</dt>
                <dd>{personName}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Mail Id</dt>
                <dd>{emailValue}</dd>
              </div>
              <div className="mcv-dl-row mcv-dl-block">
                <dt>Company Address</dt>
                <dd>{addressValue}</dd>
              </div>
            </dl>
          </section>

          {/* Auction Information */}
          <section className="mcv-card">
            <div className="mcv-card-header">
              <h2 className="mcv-card-title">
                <Calendar /> Auction Information
              </h2>
            </div>
            <dl className="mcv-dl">
              <div className="mcv-dl-row">
                <dt>Auction Type</dt>
                <dd>{auctionTypeLabel(auctionTypeValue)}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Format</dt>
                <dd>{auctionModeLabel(auctionModeValue)}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Auction Date</dt>
                <dd>
                  {auction.rawAuctionDate
                    ? formatDate(auction.rawAuctionDate)
                    : formatDate(auction.auctionDate)}
                </dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Start Time</dt>
                <dd>{auction.auctionStartTime}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>End Time</dt>
                <dd>{auction.auctionEndTime || "—"}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Duration</dt>
                <dd>{auction.duration} minutes</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Currency</dt>
                <dd>{auction.currency === "INR" ? "INR" : auction.currency}</dd>
              </div>
              <div className="mcv-dl-row">
                <dt>Pre-Bid Offer Allowed</dt>
                <dd>{auction.preBidOfferAllowed ? "Yes" : "No"}</dd>
              </div>
              {backendMeta?.basePrice !== undefined && (
                <div className="mcv-dl-row">
                  <dt>Base Price</dt>
                  <dd>
                    {auction.currency} {backendMeta.basePrice.toLocaleString()}
                  </dd>
                </div>
              )}
              {backendMeta?.currentPrice !== undefined && (
                <div className="mcv-dl-row">
                  <dt>Current Price</dt>
                  <dd>
                    {auction.currency} {backendMeta.currentPrice.toLocaleString()}
                  </dd>
                </div>
              )}
            </dl>
          </section>
        </aside>
      </div>

      {/* Modals */}
      <SuccessModal
        isOpen={successModal.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, message: '' })}
        title="Success"
        message={successModal.message}
      />
      <ErrorModal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title="Error"
        message={errorModal.message}
      />
      <InfoModal
        isOpen={infoModal.isOpen}
        onClose={() => setInfoModal({ isOpen: false, message: '' })}
        title="Information"
        message={infoModal.message}
      />
    </div>
  );
};  

export default MyCreatedAuctionView;

// --- Insert modal and handlers ---


